import React, { useState, useEffect, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import Dropdown from '../Dropdown/Dropdown'
import { fetchAllUsers } from '../../Api/user'
import { fetchPreviousMeetings } from '../../Api/meetings'
import { toast } from 'react-toastify'
import {
  createTag,
  fetchEventTags,
  fetchUserTags,
  addOrRemoveTagFromEvent,
} from '../../Api/transcriptAndSummary'

const monthsShort = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const AddTagPopup = ({ close, eventId, fetch, user }) => {
  const memberAccessButtonRef = useRef(null)
  const ownerButtonRef = useRef(null)
  const meetingsButtonRef = useRef(null)
  const tagsButtonRef = useRef(null)

  const [attendees, setAttendees] = useState([])
  const [coOwners, setCoOwners] = useState([])
  const [eventIds, setEventIds] = useState([])
  const [users, setUsers] = useState([])
  const [name, setName] = useState('')
  const [previousMeetings, setPreviousMeetings] = useState([])
  const [eventTags, setEventTags] = useState([])
  const [userTags, setUserTags] = useState([])
  const [selectedTag, setSelectedTag] = useState([])

  const fetchUsersData = useCallback(async () => {
    const apiResponse = await fetchAllUsers()
    if (apiResponse?.length > 0) {
      const temp = []
      apiResponse.forEach((item) => {
        if (item.id !== user.id) {
          temp.push({
            value: item.id,
            label: item.name ? item.name : item.email,
          })
        }
      })
      setUsers([...temp])
    } else {
      setUsers([])
    }
  }, [user])

  const fetchPreviousMeetingsData = useCallback(async () => {
    const apiResponse = await fetchPreviousMeetings()
    if (apiResponse?.length > 0) {
      const temp = []
      apiResponse.forEach((meeting) => {
        if (eventId !== meeting.event_id) {
          const date = meeting.start_time
            ? new Date(Number(meeting.start_time))
            : new Date()
          temp.push({
            value: meeting.event_id,
            label: `${meeting.title} (${
              monthsShort[date.getMonth()]
            } ${date.getDate()}, ${date.getFullYear()})`,
          })
        }
      })
      setPreviousMeetings([...temp])
    } else {
      setPreviousMeetings([])
    }
  }, [eventId])

  const fetchEventTagsData = useCallback(async () => {
    const apiResponse = await fetchEventTags(eventId)
    if (apiResponse?.length > 0) {
      setEventTags([...apiResponse])
    } else {
      setEventTags([])
    }
  }, [setEventTags, eventId])

  const fetchUserTagsData = useCallback(async () => {
    const apiResponse = await fetchUserTags(eventId)
    if (apiResponse?.length > 0) {
      setUserTags([
        ...apiResponse.map((item) => ({
          value: item.id,
          label: item.name,
        })),
      ])
    } else {
      setUserTags([])
    }
  }, [setUserTags, eventId])

  useEffect(() => {
    if (eventId) {
      fetchUsersData()
      fetchPreviousMeetingsData()
      fetchEventTagsData()
      fetchUserTagsData()
    }
  }, [
    fetchUsersData,
    fetchPreviousMeetingsData,
    fetchEventTagsData,
    fetchUserTagsData,
    eventId,
  ])

  const addOrRemoveTagFromEventHandler = useCallback(
    async (tagId, requestType) => {
      if (eventId) {
        const apiResponse = await addOrRemoveTagFromEvent(
          tagId,
          eventId,
          requestType
        )
        if (apiResponse) {
          toast.success(apiResponse)
          fetchEventTagsData()
          fetchUserTagsData()
          setSelectedTag([])
          fetch()
        }
      }
    },
    [eventId, fetchEventTagsData, fetchUserTagsData, fetch]
  )

  useEffect(() => {
    if (selectedTag?.length > 0) {
      addOrRemoveTagFromEventHandler(selectedTag[0], 1)
    }
  }, [selectedTag, addOrRemoveTagFromEventHandler])

  const submitHandler = useCallback(
    async (event) => {
      event.preventDefault()
      const cleanData = {
        name: name.trim(),
        owners: [user.id, ...coOwners].join(','),
        members: attendees.join(','),
        event_ids: [eventId, ...eventIds].join(','),
      }
      if (cleanData.name === '') {
        toast.error('Tag name is reuired')
      } else {
        const apiResponse = await createTag(cleanData)
        if (apiResponse) {
          toast.success('Tag created successfully')
          fetch()
          close(false)
        }
      }
    },
    [name, fetch, coOwners, attendees, eventIds, close, eventId, user]
  )
  return (
    <div className='add-tag-popup-container'>
      <div className='add-tag-popup'>
        <button className='close' onClick={() => close(false)}>
          X
        </button>
        <section>
          <h3>Create New Tag</h3>
          <form onSubmit={submitHandler}>
            <div className='input-box'>
              <input
                placeholder='*Tag name'
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className='input-box'>
              <div className='filter'>
                <button ref={memberAccessButtonRef} type='button'>
                  {attendees?.length > 0
                    ? users
                      .filter((user) => attendees.indexOf(user.value) !== -1)
                      .map((el) => {
                        const name = el.label
                          .split(' ')
                          .filter((el1) => el1?.trim() !== '')
                        if (name) {
                          if (name.length > 1) {
                            return (
                              <span key={el.value}>
                                {name[0].substring(0, 1) +
                                    name[1].substring(0, 1)}
                              </span>
                            )
                          } else {
                            return (
                              <span key={el.value}>
                                {name[0].substring(0, 1)}
                              </span>
                            )
                          }
                        } else {
                          return <></>
                        }
                      })
                    : 'Members with access'}
                </button>
                <Dropdown
                  isSearchable={true}
                  isMultiple={true}
                  data={users}
                  setActiveFilters={setAttendees}
                  activeFilters={attendees}
                  ref={memberAccessButtonRef}
                />
              </div>
              <span>*Who else can view / edit tag content?</span>
            </div>
            <div className='input-box'>
              <div className='filter'>
                <button ref={ownerButtonRef} type='button'>
                  {coOwners?.length > 0
                    ? users
                      .filter((user) => coOwners.indexOf(user.value) !== -1)
                      .map((el) => {
                        const name = el.label
                          .split(' ')
                          .filter((el1) => el1?.trim() !== '')
                        if (name) {
                          if (name.length > 1) {
                            return (
                              <span key={el.value}>
                                {name[0].substring(0, 1) +
                                    name[1].substring(0, 1)}
                              </span>
                            )
                          } else {
                            return (
                              <span key={el.value}>
                                {name[0].substring(0, 1)}
                              </span>
                            )
                          }
                        } else {
                          return <></>
                        }
                      })
                    : 'Owners'}
                </button>
                <Dropdown
                  isSearchable={true}
                  isMultiple={true}
                  data={users}
                  setActiveFilters={setCoOwners}
                  activeFilters={coOwners}
                  ref={ownerButtonRef}
                />
              </div>
              <span>*Who can edit members (besides you)?</span>
            </div>
            <div className='input-box'>
              <div className='filter'>
                <button ref={meetingsButtonRef} type='button'>
                  Add previous meetings
                </button>
                <Dropdown
                  isSearchable={true}
                  isMultiple={true}
                  data={previousMeetings}
                  setActiveFilters={setEventIds}
                  activeFilters={eventIds}
                  ref={meetingsButtonRef}
                />
              </div>
              <span>*Who can see members (besides you)?</span>
            </div>
            <button className='submit-button'>Add</button>
          </form>
        </section>
        <section>
          <h3>Add Existing Tag</h3>
          <div className='existing-tags'>
            {
              eventTags.length > 0 &&
              eventTags.map((tag) => (
                <div key={tag._id} className='tag-buttons'>
                  <button
                    onClick={() => addOrRemoveTagFromEventHandler(tag.id, 0)}
                    className='active'
                  >{`<${tag.name}>`}</button>
                </div>
              ))
            }
            <div className='filter'>
              <button ref={tagsButtonRef} type='button'>
                Add others
              </button>
              <Dropdown
                isSearchable={true}
                isMultiple={true}
                data={userTags}
                setActiveFilters={setSelectedTag}
                activeFilters={selectedTag}
                ref={tagsButtonRef}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user.data,
})

export default connect(mapStateToProps, null)(AddTagPopup)
